import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Eye, EyeOff, Globe, MessageCircle, Palette, Users } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { trackEvent } from "@/lib/ga4";
import { getLandingAttribution } from "@/lib/landingAttribution";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ConsumerOnboardingPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, "consumer", undefined, fullName);
      // Same GA4 sign_up (the Google Ads "sign_up" conversion) as /signup.
      trackEvent("sign_up", { method: "email", account_type: "consumer", source: "consumer_onboarding", ...getLandingAttribution() });
      navigate("/consumer");  // Navigate to separate consumer experience
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <MessageCircle className="h-10 w-10 text-blue-600" />,
      title: "Personal AI Assistant",
      description: "Get instant help with your products, devices, and services - anytime, anywhere.",
    },
    {
      icon: <Clock className="h-10 w-10 text-blue-600" />,
      title: "24/7 Availability",
      description: "Never wait for business hours. Get support whenever you need it most.",
    },
    {
      icon: <Users className="h-10 w-10 text-blue-600" />,
      title: "Expert Knowledge",
      description: "Access expert-level guidance trained on thousands of product manuals and guides.",
    },
    {
      icon: <CheckCircle2 className="h-10 w-10 text-blue-600" />,
      title: "Instant Solutions",
      description: "Get step-by-step solutions tailored to your specific product and situation.",
    },
  ];

  const consumerFeatures = [
    {
      icon: <Palette className="h-6 w-6 text-blue-600" />,
      title: "Personalized Help",
      text: "Get assistance tailored to your specific products and preferences",
    },
    {
      icon: <MessageCircle className="h-6 w-6 text-blue-600" />,
      title: "Easy Communication",
      text: "Chat naturally - no technical jargon or complicated menus",
    },
    {
      icon: <Globe className="h-6 w-6 text-blue-600" />,
      title: "Multi-Device Support",
      text: "Get help with appliances, electronics, vehicles, and more - all in one place",
    },
    {
      icon: <Clock className="h-6 w-6 text-blue-600" />,
      title: "Instant Responses",
      text: "No more waiting on hold or searching through manuals",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-center justify-center text-white">
        <div className="absolute inset-0 z-0">
          <img src="/hero-background.webp" alt="Modern home with smart devices" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60 z-10"></div>
        </div>

        <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Hero content */}
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start mb-6">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                  Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Instant Help</span> for
                  Any Product
                </h1>
              </div>

              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Join thousands of users who get expert-level support for their appliances, electronics, vehicles, and more - available 24/7.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>Instant expert guidance</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>Works with any product</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>Available 24/7</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>No waiting on hold</span>
                </div>
              </div>
            </div>

            {/* Right side - Sign up form */}
            <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-2xl relative z-30">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[#222B45] mb-4">Start Getting Help Today</h2>
                <p className="text-lg text-[#6B7280]">Create your free account and get instant access to expert support</p>
              </div>

              <form
                name="helpgenie_consumer_onboarding"
                id="helpgenie_consumer_onboarding"
                data-hs-do-not-collect="true"
                onSubmit={handleSubmit}
                className="space-y-6 relative z-40"
              >
                <div>
                  <Label htmlFor="fullName" className="text-[#222B45]">
                    Full Name
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    autoComplete="name"
                    className="mt-1 relative z-50 text-gray-900"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#222B45]">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="mt-1 relative z-50 text-gray-900"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#222B45]">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a password (min 6 characters)"
                      required
                      className="mt-1 relative z-50 text-gray-900 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-50"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-[#222B45]">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                      className="mt-1 relative z-50 text-gray-900 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-50"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && <div className="text-sm text-red-500 text-center p-3 bg-red-50 rounded-md">{error}</div>}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 text-lg font-semibold"
                >
                  {loading ? "Creating Account..." : "Get Started Free"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <div className="text-center text-sm text-[#6B7280]">
                  Already have an account?{" "}
                  <button type="button" onClick={() => navigate("/signin")} className="text-blue-600 hover:text-blue-700 font-medium">
                    Sign in
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Your Personal Support Assistant</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Never struggle with product issues again. Get instant, expert-level help for all your devices and appliances.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-300 text-center transform hover:-translate-y-1"
              >
                <div className="inline-block bg-blue-100 p-3 rounded-full mb-4">{feature.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consumer Features Section */}
      <section className="w-full bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-6">
              Support That
              <br />
              Actually Helps.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Every Time.</span>
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              No more digging through manuals, waiting on hold, or getting frustrated with unhelpful chatbots. Get real solutions that work.
            </p>
          </div>

          <div className="w-full space-y-4">
            {consumerFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-4 shadow-sm flex items-center border-l-4 border-blue-500 hover:shadow-md transition-all duration-300"
              >
                <div className="bg-blue-50 p-2 rounded-full mr-4">{feature.icon}</div>
                <div>
                  <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="w-full bg-gradient-to-r from-blue-600 to-purple-600 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Ready to Get the Help You Deserve?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of users who never struggle with product issues anymore. Get instant expert help - completely free.
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
          >
            Start Getting Help
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
